import React, { useState } from "react";
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Stack,
  Box,
  Paper,
  Link,
  Divider,
  IconButton,
  InputAdornment,
  Alert,
  alpha,
  Checkbox,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Tooltip
} from "@mui/material";
import { motion } from "framer-motion";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import DiamondIcon from '@mui/icons-material/Diamond';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import GoogleIcon from '@mui/icons-material/Google';
import FacebookIcon from '@mui/icons-material/Facebook';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhoneIcon from '@mui/icons-material/Phone';
import VerifiedIcon from '@mui/icons-material/Verified';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const steps = ['Account Details', 'Personal Information', 'Verification'];

const Register = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [accountType, setAccountType] = useState('collector');
  const [formData, setFormData] = useState({
    // Account Details
    email: '',
    password: '',
    confirmPassword: '',
    // Personal Information
    firstName: '',
    lastName: '',
    phone: '',
    country: '',
    // Verification
    agreeTerms: false,
    newsletter: false
  });
  const [errors, setErrors] = useState({});
  const [registerError, setRegisterError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'agreeTerms' || name === 'newsletter' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep = () => {
    const newErrors = {};
    
    if (activeStep === 0) {
      if (!formData.email) newErrors.email = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
      
      if (!formData.password) newErrors.password = 'Password is required';
      else if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
      else if (!/(?=.*[A-Za-z])(?=.*\d)/.test(formData.password)) {
        newErrors.password = 'Password must contain letters and numbers';
      }
      
      if (!formData.confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
      else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (activeStep === 1) {
      if (!formData.firstName) newErrors.firstName = 'First name is required';
      if (!formData.lastName) newErrors.lastName = 'Last name is required';
      if (!formData.phone) newErrors.phone = 'Phone number is required';
      if (!formData.country) newErrors.country = 'Country is required';
    }
    
    if (activeStep === 2) {
      if (!formData.agreeTerms) newErrors.agreeTerms = 'You must agree to the terms';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      if (activeStep === steps.length - 1) {
        handleRegister();
      } else {
        setActiveStep((prev) => prev + 1);
      }
    }
  };

  const handleRegister = () => {
    // Simulate registration
    console.log('Registration complete:', { ...formData, accountType });
    
    // Show success state
    setRegisterSuccess(true);
    
    // Redirect to login after 2 seconds
    setTimeout(() => {
      navigate('/login');
    }, 2000);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setErrors({});
  };

  const getPasswordStrength = () => {
    const { password } = formData;
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength++;
    
    if (strength <= 2) return { label: 'Weak', color: '#f44336' };
    if (strength <= 3) return { label: 'Medium', color: '#FFA726' };
    if (strength <= 4) return { label: 'Strong', color: '#4CAF50' };
    return { label: 'Very Strong', color: '#2E7D32' };
  };

  const passwordStrength = getPasswordStrength();

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Stack spacing={3}>
            <TextField
              fullWidth
              name="email"
              label="Email Address"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: errors.email ? '#f44336' : '#9E9E9E' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#F5F5F5',
                  '&:hover': { backgroundColor: '#FFFFFF' },
                },
              }}
            />

            <TextField
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={errors.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: errors.password ? '#f44336' : '#9E9E9E' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: '#9E9E9E' }}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#F5F5F5',
                  '&:hover': { backgroundColor: '#FFFFFF' },
                },
              }}
            />

            {/* Password Strength Indicator */}
            {formData.password && (
              <Box sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" color="#616161">
                    Password strength:
                  </Typography>
                  <Chip 
                    label={passwordStrength.label}
                    size="small"
                    sx={{ 
                      backgroundColor: alpha(passwordStrength.color, 0.1),
                      color: passwordStrength.color,
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 20,
                    }}
                  />
                </Box>
                <Stack direction="row" spacing={1}>
                  {[1, 2, 3, 4].map((level) => (
                    <Box
                      key={level}
                      sx={{
                        flex: 1,
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: level <= 
                          (formData.password.length >= 8 ? 1 : 0) +
                          (formData.password.length >= 12 ? 1 : 0) +
                          (/[A-Z]/.test(formData.password) ? 1 : 0) +
                          (/[0-9]/.test(formData.password) ? 1 : 0)
                          ? passwordStrength.color
                          : alpha('#9E9E9E', 0.2),
                        transition: 'all 0.3s ease',
                      }}
                    />
                  ))}
                </Stack>
                <Typography variant="caption" color="#9E9E9E" sx={{ display: 'block', mt: 1 }}>
                  Include uppercase letters, numbers, and symbols for a stronger password
                </Typography>
              </Box>
            )}

            <TextField
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: errors.confirmPassword ? '#f44336' : '#9E9E9E' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      sx={{ color: '#9E9E9E' }}
                    >
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#F5F5F5',
                  '&:hover': { backgroundColor: '#FFFFFF' },
                },
              }}
            />
          </Stack>
        );
      
      case 1:
        return (
          <Stack spacing={3}>
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                name="firstName"
                label="First Name"
                value={formData.firstName}
                onChange={handleChange}
                error={!!errors.firstName}
                helperText={errors.firstName}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: errors.firstName ? '#f44336' : '#9E9E9E' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F5F5F5',
                    '&:hover': { backgroundColor: '#FFFFFF' },
                  },
                }}
              />

              <TextField
                fullWidth
                name="lastName"
                label="Last Name"
                value={formData.lastName}
                onChange={handleChange}
                error={!!errors.lastName}
                helperText={errors.lastName}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: errors.lastName ? '#f44336' : '#9E9E9E' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F5F5F5',
                    '&:hover': { backgroundColor: '#FFFFFF' },
                  },
                }}
              />
            </Stack>

            <TextField
              fullWidth
              name="phone"
              label="Phone Number"
              value={formData.phone}
              onChange={handleChange}
              error={!!errors.phone}
              helperText={errors.phone}
              placeholder="+1 (555) 000-0000"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon sx={{ color: errors.phone ? '#f44336' : '#9E9E9E' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#F5F5F5',
                  '&:hover': { backgroundColor: '#FFFFFF' },
                },
              }}
            />

            <TextField
              fullWidth
              name="country"
              label="Country"
              value={formData.country}
              onChange={handleChange}
              error={!!errors.country}
              helperText={errors.country}
              placeholder="Select your country"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#F5F5F5',
                  '&:hover': { backgroundColor: '#FFFFFF' },
                },
              }}
            />
          </Stack>
        );
      
      case 2:
        return (
          <Stack spacing={3}>
            {/* Success Alert */}
            {registerSuccess && (
              <Alert 
                severity="success"
                icon={<CheckCircleIcon fontSize="inherit" />}
                sx={{ 
                  mb: 2,
                  backgroundColor: alpha('#4CAF50', 0.05),
                  color: '#2E7D32',
                  border: '1px solid',
                  borderColor: alpha('#4CAF50', 0.2),
                }}
              >
                Registration successful! Redirecting to login...
              </Alert>
            )}

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ color: '#2D2D2D', mb: 2 }}>
                Account Type
                <Tooltip title="Select the option that best describes you" arrow>
                  <InfoOutlinedIcon sx={{ fontSize: 14, ml: 0.5, color: '#9E9E9E' }} />
                </Tooltip>
              </Typography>
              <Stack direction="row" spacing={2}>
                {[
                  { value: 'collector', label: 'Private Collector', icon: '👤' },
                  { value: 'dealer', label: 'Dealer', icon: '🏢' },
                  { value: 'investor', label: 'Investor', icon: '📈' },
                ].map((type) => (
                  <Button
                    key={type.value}
                    variant={accountType === type.value ? 'contained' : 'outlined'}
                    onClick={() => setAccountType(type.value)}
                    fullWidth
                    sx={{
                      py: 1.5,
                      borderColor: alpha('#9E9E9E', 0.3),
                      color: accountType === type.value ? '#000000' : '#616161',
                      backgroundColor: accountType === type.value ? 'primary.main' : 'transparent',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: accountType === type.value 
                          ? 'primary.dark' 
                          : alpha('#C6A75E', 0.04),
                      },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {type.icon} {type.label}
                    </Typography>
                  </Button>
                ))}
              </Stack>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  name="agreeTerms"
                  checked={formData.agreeTerms}
                  onChange={handleChange}
                  sx={{
                    color: errors.agreeTerms ? '#f44336' : '#9E9E9E',
                    '&.Mui-checked': {
                      color: 'primary.main',
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" color={errors.agreeTerms ? '#f44336' : '#616161'}>
                  I agree to the{' '}
                  <Link
                    component={RouterLink}
                    to="/terms"
                    sx={{ color: 'primary.main', textDecoration: 'none' }}
                  >
                    Terms of Service
                  </Link>
                  {' '}and{' '}
                  <Link
                    component={RouterLink}
                    to="/privacy"
                    sx={{ color: 'primary.main', textDecoration: 'none' }}
                  >
                    Privacy Policy
                  </Link>
                </Typography>
              }
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="newsletter"
                  checked={formData.newsletter}
                  onChange={handleChange}
                  sx={{
                    color: '#9E9E9E',
                    '&.Mui-checked': {
                      color: 'primary.main',
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" color="#616161">
                  I'd like to receive exclusive offers and market insights
                </Typography>
              }
            />

            {/* Verification Badge */}
            <Paper
              sx={{
                p: 3,
                backgroundColor: alpha('#C6A75E', 0.05),
                border: '1px solid',
                borderColor: 'primary.main',
                borderRadius: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <VerifiedIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ color: '#2D2D2D', fontWeight: 600 }}>
                    Verified Member Benefits
                  </Typography>
                  <Typography variant="caption" color="#616161">
                    Complete your profile to unlock premium features
                  </Typography>
                </Box>
              </Box>
              <Stack spacing={1}>
                {[
                  'Priority access to rare coin listings',
                  'Exclusive market insights and reports',
                  'Dedicated concierge support',
                  'Invitations to private auctions'
                ].map((benefit, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 16 }} />
                    <Typography variant="caption" color="#616161">
                      {benefit}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Stack>
        );
      
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F5F5F5',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Back to Home */}
          <Box sx={{ mb: 3 }}>
            <Button
              component={RouterLink}
              to="/"
              startIcon={<ArrowBackIcon />}
              sx={{
                color: '#616161',
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: 'transparent',
                },
              }}
            >
              Back to Home
            </Button>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, md: 6 },
              backgroundColor: '#FFFFFF',
              border: '1px solid',
              borderColor: alpha('#9E9E9E', 0.15),
              boxShadow: '0 8px 24px rgba(0,0,0,0.02)',
              borderRadius: 2,
            }}
          >
            {/* Logo */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                component={RouterLink}
                to="/"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  textDecoration: 'none',
                  mb: 2,
                }}
              >
                <DiamondIcon sx={{ color: 'primary.main', fontSize: 40 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#2D2D2D' }}>
                  DIAMOND<span style={{ color: '#C6A75E' }}>COIN</span>
                </Typography>
              </Box>
              <Typography variant="body2" color="#616161">
                Join the world's most exclusive numismatic community
              </Typography>
            </Box>

            {/* Stepper */}
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel
                    StepIconProps={{
                      sx: {
                        '& .MuiStepIcon-root': {
                          color: alpha('#9E9E9E', 0.3),
                          '&.Mui-active': {
                            color: 'primary.main',
                          },
                          '&.Mui-completed': {
                            color: 'primary.main',
                          },
                        },
                      },
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#616161' }}>
                      {label}
                    </Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Error Alert */}
            {registerError && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  backgroundColor: alpha('#f44336', 0.05),
                  color: '#f44336',
                  border: '1px solid',
                  borderColor: alpha('#f44336', 0.2),
                }}
              >
                {registerError}
              </Alert>
            )}

            {/* Form Content */}
            <Box component="form" onSubmit={(e) => e.preventDefault()}>
              {getStepContent(activeStep)}

              {/* Navigation Buttons */}
              <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
                {activeStep > 0 && (
                  <Button
                    onClick={handleBack}
                    variant="outlined"
                    fullWidth
                    disabled={registerSuccess}
                    sx={{
                      borderColor: alpha('#9E9E9E', 0.3),
                      color: '#616161',
                      '&:hover': {
                        borderColor: 'primary.main',
                        color: 'primary.main',
                      },
                    }}
                  >
                    Back
                  </Button>
                )}
                <Button
                  onClick={handleNext}
                  variant="contained"
                  fullWidth
                  disabled={registerSuccess}
                  sx={{ 
                    py: 1.5,
                    background: 'linear-gradient(135deg, #C6A75E 0%, #9E823C 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #D4B77A 0%, #C6A75E 100%)',
                    },
                  }}
                >
                  {activeStep === steps.length - 1 ? 'Create Account' : 'Continue'}
                </Button>
              </Stack>

              {/* Social Registration */}
              {activeStep === 0 && (
                <>
                  <Box sx={{ position: 'relative', my: 4 }}>
                    <Divider sx={{ borderColor: alpha('#9E9E9E', 0.15) }}>
                      <Typography variant="caption" sx={{ color: '#9E9E9E', px: 2 }}>
                        Or sign up with
                      </Typography>
                    </Divider>
                  </Box>

                  <Stack direction="row" spacing={2}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<GoogleIcon />}
                      sx={{
                        borderColor: alpha('#9E9E9E', 0.3),
                        color: '#616161',
                        '&:hover': {
                          borderColor: 'primary.main',
                          color: 'primary.main',
                          backgroundColor: alpha('#C6A75E', 0.04),
                        },
                      }}
                    >
                      Google
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<FacebookIcon />}
                      sx={{
                        borderColor: alpha('#9E9E9E', 0.3),
                        color: '#616161',
                        '&:hover': {
                          borderColor: 'primary.main',
                          color: 'primary.main',
                          backgroundColor: alpha('#C6A75E', 0.04),
                        },
                      }}
                    >
                      Facebook
                    </Button>
                  </Stack>
                </>
              )}

              {/* Login Link */}
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Typography variant="body2" color="#616161" display="inline">
                  Already have an account?{' '}
                </Typography>
                <Link
                  component={RouterLink}
                  to="/login"
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Sign In
                </Link>
              </Box>
            </Box>
          </Paper>

          {/* Trust Badges */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
              <Typography variant="caption" sx={{ color: '#9E9E9E' }}>
                🔒 Secure Registration
              </Typography>
              <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#9E9E9E', my: 'auto' }} />
              <Typography variant="caption" sx={{ color: '#9E9E9E' }}>
                🛡️ Data Protection
              </Typography>
              <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#9E9E9E', my: 'auto' }} />
              <Typography variant="caption" sx={{ color: '#9E9E9E' }}>
                ✅ GDPR Compliant
              </Typography>
            </Stack>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Register;